import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useTheme } from "@mui/material";
import { BarStack } from '@visx/shape';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { LegendOrdinal, LegendItem, LegendLabel } from '@visx/legend';
import { defaultPieChartColorRange } from '@components/pie-chart/utils';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { getChartTooltipStyles, getChartLegendBoxStyles, chartTooltipRowStyle, getChartTooltipSwatchStyle } from '@utils/chart-styles';


const LINE_HEIGHT = 17;
const BASE_BOTTOM_MARGIN = 45; // Base margin for axis line, tick marks, and single-line label
const MAX_DISPLAYED_WORDS = 2; // Truncate labels to first 2 words if more than 2

// Calculate dynamic bottom margin based on max words in labels (capped at maxDisplayedWords)
const calculateBottomMargin = (labels: string[]) => {
  const maxWords = Math.max(...labels.map(label => {
    const wordCount = String(label).split(" ").length;
    return wordCount > MAX_DISPLAYED_WORDS ? MAX_DISPLAYED_WORDS : wordCount;
  }), 1);
  return BASE_BOTTOM_MARGIN + (maxWords - 1) * LINE_HEIGHT;
};

// Custom tick component for wrapping multi-word labels
const CustomTickLabel = ({ 
  x = 0, 
  y = 0, 
  text, 
  fill, 
  fontSize,
  textAnchor 
}: any) => {
  const text_ = String(text);
  let words = text_.split(" ");
  // Truncate to first 2 words if more than 2
  if (words.length > MAX_DISPLAYED_WORDS) {
    words = words.slice(0, MAX_DISPLAYED_WORDS);
  }
  const startY = y + (words.length - 1) * (LINE_HEIGHT / 2);

  return (
    <text x={x} y={startY} fill={fill} fontSize={fontSize} textAnchor={textAnchor}>
      {words.map((word: string, idx: number) => (
        <tspan key={`word-${idx}`} x={x} dy={idx === 0 ? 0 : LINE_HEIGHT}>
          {word}
        </tspan>
      ))}
    </text>
  );
};

// Vertical tick label component for narrow widths with many labels
const VerticalTickLabel = ({ 
  x = 0, 
  y = 0, 
  text, 
  fill, 
  fontSize
}: any) => {
  const text_ = String(text);
  let words = text_.split(" ");
  // Truncate to first 2 words if more than 2
  if (words.length > MAX_DISPLAYED_WORDS) {
    words = words.slice(0, MAX_DISPLAYED_WORDS);
  }
  const verticalLineHeight = 14;
  
  return (
    <g transform={`rotate(-90, ${x}, ${y})`}>
      <text 
        x={x} 
        y={y} 
        fill={fill} 
        fontSize={fontSize} 
        textAnchor="end"
        dy="0.35em"
        dx="-0.5em"
      >
        {words.map((word: string, idx: number) => (
          <tspan key={`word-${idx}`} x={x} dx="-0.5em" dy={idx === 0 ? 0 : verticalLineHeight}>
            {word}
          </tspan>
        ))}
      </text>
    </g>
  );
};

export interface StackedBarChartProps {
  data: Array<Record<string, string | number>>;
  keys: string[]; // keys to stack (statuses)
  xKey: string; // device type key
  colors?: string[];
  minHeight?: number;
  keyLabels?: Record<string, string>; // Optional: map keys to display labels
}

export const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data,
  keys,
  xKey,
  colors = defaultPieChartColorRange,
  minHeight = 300,
  keyLabels = {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const { width, height } = container.getBoundingClientRect();
      setDimensions({ 
        width, 
        height: Math.max(height, minHeight) 
      });
    };

    // Initial measurement after layout
    updateDimensions();

    // Set up ResizeObserver to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [minHeight]);

  return (
    <div 
      ref={containerRef} 
      style={{ width: '100%', minHeight: `${minHeight}px`, height: '100%' }}
    >
      {dimensions.width > 0 && (
        <StackedBarChartInner
          data={data}
          keys={keys}
          xKey={xKey}
          colors={colors}
          width={dimensions.width}
          height={dimensions.height}
          keyLabels={keyLabels}
        />
      )}
    </div>
  );
};

interface StackedBarChartInnerProps {
  data: Array<Record<string, string | number>>;
  keys: string[];
  xKey: string;
  colors: string[];
  width: number;
  height: number;
  keyLabels?: Record<string, string>;
}

interface TooltipData {
  category: string;
  key: string;
  value: number;
  color: string;
}

const StackedBarChartInner: React.FC<StackedBarChartInnerProps> = ({
  data,
  keys,
  xKey,
  colors,
  width,
  height,
  keyLabels = {},
}) => {
  const theme = useTheme();
  const tooltipStyles = getChartTooltipStyles(theme);
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
  } = useTooltip<TooltipData>();

  // Calculate dynamic bottom margin based on label word count
  const labels = data.map(d => String(d[xKey]));
  
  // Determine if we should use vertical labels
  const shouldUseVerticalLabels = labels.length > 6 && width < 900;
  
  const bottomMargin = useMemo(() => 
    shouldUseVerticalLabels ? 120 : calculateBottomMargin(labels), 
    [labels, shouldUseVerticalLabels]
  );
  const isNarrow = width < 700;
  const legendWidth = isNarrow ? 0 : 130;
  const chartWidth = Math.max(width - legendWidth, 200);
  const legendBottomSpace = isNarrow ? (keys.length > 8 ? 75 : 60) : 0;
  const chartHeight = height - legendBottomSpace;
  const margin = { top: 20, right: 20, bottom: bottomMargin, left: 60 };
  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;

  const xScale = scaleBand<string>({
    domain: data.map(d => d[xKey] as string),
    padding: 0.4,
    range: [0, innerWidth],
  });
  const yMax = Math.max(
    ...data.map(d => keys.reduce((sum, key) => sum + Number(d[key] || 0), 0)),
    1
  );
  const yScale = scaleLinear<number>({
    domain: [0, yMax],
    nice: true,
    range: [innerHeight, 0],
  });
  const colorScale = scaleOrdinal<string, string>({
    domain: keys,
    range: colors,
  });

  // Handle bar hover
  const handleBarHover = useCallback(
    (event: React.MouseEvent<SVGRectElement>, bar: any) => {
      const point = localPoint(event);
      if (!point) return;

      const category = bar.bar.data[xKey] as string;
      const value = bar.bar.data[bar.key] as number;
      
      showTooltip({
        tooltipData: {
          category,
          key: bar.key,
          value,
          color: bar.color,
        },
        tooltipLeft: point.x,
        tooltipTop: point.y,
      });
    },
    [xKey, showTooltip]
  );

  return (
    <div style={{ display: "flex", flexDirection: isNarrow ? "column" : "row", width: "100%", height: "100%", gap: "0", alignItems: isNarrow ? "center" : "flex-start", position: "relative" }}>
      <svg
        width={chartWidth}
        height={chartHeight}
        style={{ display: "block", flexShrink: 0 }}
      >
        <defs>
          <filter id="bar-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
          </filter>
        </defs>
        <g transform={`translate(${margin.left},${margin.top})`}>
          <BarStack
            data={data}
            keys={keys}
            x={d => d[xKey] as string}
            xScale={xScale}
            yScale={yScale}
            color={colorScale}
          >
            {(barStacks) =>
              barStacks.map(barStack =>
                barStack.bars.map((bar) => {
                  const isHovered = tooltipOpen && tooltipData?.category === bar.bar.data[xKey] && tooltipData?.key === bar.key;
                  return (
                    <rect
                      key={bar.key + '-' + bar.index}
                      x={bar.x}
                      y={bar.y}
                      width={bar.width}
                      height={bar.height}
                      fill={bar.color}
                      style={{ cursor: 'pointer', filter: isHovered ? 'url(#bar-shadow)' : 'none' }}
                      onMouseMove={(e) => handleBarHover(e, bar)}
                      onMouseLeave={hideTooltip}
                    />
                  );
                })
              )
            }
          </BarStack>
          <AxisLeft scale={yScale} left={0} />
          <AxisBottom 
            scale={xScale} 
            top={innerHeight}
            tickComponent={({ formattedValue, ...tickProps }) => 
              shouldUseVerticalLabels ? (
                <VerticalTickLabel
                  {...tickProps}
                  text={formattedValue}
                  fill="currentColor"
                  fontSize={11}
                />
              ) : (
                <CustomTickLabel
                  {...tickProps}
                  text={formattedValue}
                  fill="currentColor"
                  fontSize={11}
                  textAnchor="middle"
                />
              )
            }
          />
        </g>
      </svg>
      {/* Legend to the right of chart */}
      <div style={{ 
        display: "flex", 
        flexDirection: isNarrow ? "row" : "column",
        flexWrap: isNarrow ? "wrap" : "nowrap",
        justifyContent: isNarrow ? "center" : "flex-start", 
        padding: "4px",
        ...getChartLegendBoxStyles(theme),
        borderRadius: "4px",
        minWidth: isNarrow ? "auto" : "100px",
        maxWidth: isNarrow ? "100%" : "120px",
        fontSize: "10px",
        gap: isNarrow ? "8px" : "0",
      }}>
        <LegendOrdinal
          scale={colorScale}
          direction={isNarrow ? "row" : "column"}
          labelFormat={id => keyLabels[id] || id}
        >
          {(labels) =>
            labels.map((label, i) => (
              <LegendItem key={i} margin={isNarrow ? "0 8px 0 0" : "0 0 4px 0"}>
                <svg width={10} height={10} style={{ marginRight: 3, flexShrink: 0 }}>
                  <rect fill={label.value} width={10} height={10} />
                </svg>
                <LegendLabel style={{ fontSize: "10px", wordWrap: "break-word", overflowWrap: "break-word" }}>{label.text}</LegendLabel>
              </LegendItem>
            ))
          }
        </LegendOrdinal>
      </div>
      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          left={tooltipLeft}
          top={tooltipTop}
          style={tooltipStyles}
        >
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            {tooltipData.category}
          </div>
          <div style={{ ...chartTooltipRowStyle, marginTop: '4px' }}>
            <span style={getChartTooltipSwatchStyle(tooltipData.color)} />
            <span style={{ fontSize: '12px' }}>{keyLabels[tooltipData.key] || tooltipData.key}:</span>
            <span style={{ fontSize: '12px', fontWeight: 500 }}>{tooltipData.value.toLocaleString()}</span>
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};
